/**
 * Attempt voice contract (client side).
 *
 * The backend resolves `ttsVoice`, `ttsProvider` and `genderPreference` once when
 * the attempt starts, persists them on the attempt and replays them on every later
 * turn. The client's job is to consume those values, never to re-derive them —
 * otherwise the persona can change voice or switch synthesis engine mid-session.
 *
 * Local persona resolution survives only as a backward-compatible fallback for
 * legacy responses that carry no contract.
 *
 * Deliberately dependency-free so it can be unit tested outside the React runtime.
 */

export interface AttemptVoiceContract {
  ttsVoice: string;
  ttsProvider: string;
  genderPreference: string;
  language?: string;
  country?: string;
  resolvedAt?: string;
  source?: string;
}

export interface VoiceFields {
  ttsVoice?: string;
  ttsProvider?: string;
  genderPreference?: string;
  country?: string;
}

export interface ResolvedVoiceFields extends VoiceFields {
  /** True when the values came from the backend contract instead of local guessing. */
  isBackendAuthoritative: boolean;
}

/** Voice names that only the OpenAI engine accepts. */
const OPENAI_VOICE_NAMES = [
  'alloy', 'ash', 'ballad', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer', 'verse',
];

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    const str = asNonEmptyString(value);
    if (str) return str;
  }
  return undefined;
}

/**
 * Derives the engine from the voice itself. A backend that sent `nova` cannot have
 * meant browser synthesis, so an absent provider field must not downgrade playback.
 */
export function inferProviderFromVoice(voice?: string): string | undefined {
  const name = asNonEmptyString(voice);
  if (!name) return undefined;
  if (OPENAI_VOICE_NAMES.includes(name.toLowerCase())) return 'openai';
  return undefined;
}

/**
 * Reads the contract out of one backend authority. `carriers` are the several
 * objects of a single response that may hold it (the response root, its
 * `initialTurn`/`aiTurn`, its `attemptSnapshot`), so they are merged field-wise.
 *
 * Returns null when the authority carries no resolved voice, which is what marks a
 * legacy response.
 */
export function readBackendVoiceContract(carriers: unknown | unknown[]): VoiceFields | null {
  const list = (Array.isArray(carriers) ? carriers : [carriers])
    .filter((c): c is Record<string, unknown> => Boolean(c) && typeof c === 'object');
  if (!list.length) return null;

  const pick = (read: (carrier: Record<string, unknown>) => unknown): string | undefined => {
    for (const carrier of list) {
      const contract = carrier.voiceContract;
      if (contract && typeof contract === 'object') {
        const fromContract = asNonEmptyString(read(contract as Record<string, unknown>));
        if (fromContract) return fromContract;
      }
      const direct = asNonEmptyString(read(carrier));
      if (direct) return direct;
    }
    return undefined;
  };

  const ttsVoice = pick((c) => firstString(c.effectiveTtsVoice, c.ttsVoice));
  if (!ttsVoice) return null;

  return {
    ttsVoice,
    // An absent engine is inferred from the voice, never borrowed from local config.
    ttsProvider: pick((c) => firstString(c.effectiveTtsProvider, c.effectiveTTSProvider, c.ttsProvider))
      || inferProviderFromVoice(ttsVoice),
    genderPreference: pick((c) => firstString(c.effectiveGenderPreference, c.genderPreference)),
    country: pick((c) => firstString(c.country)),
  };
}

/**
 * Resolves the voice fields to render a persona turn with.
 *
 * `authorities` are backend sources in descending precedence; the first one that
 * carries a contract is adopted as a unit. Mixing is forbidden on purpose: pairing a
 * backend voice with a locally guessed provider is exactly how a session silently
 * switches engines when one field is missing.
 */
export function resolveEffectiveVoiceFields(
  authorities: Array<unknown | unknown[]>,
  localFallback: VoiceFields = {}
): ResolvedVoiceFields {
  for (const authority of authorities) {
    const contract = readBackendVoiceContract(authority);
    if (contract) {
      return { ...contract, isBackendAuthoritative: true };
    }
  }
  return { ...localFallback, isBackendAuthoritative: false };
}
